import { IconCategory, IconFolder, IconPackage } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useActiveOrganization } from "@/hooks/use-organizations";
import { Skeleton } from "@/components/ui/skeleton";

const promotionTypes = [
  {
    titleKey: "menu.promotions.custom.title",
    descriptionKey: "menu.promotions.custom.table.description",
    url: "/menu/promotions/custom",
    icon: IconPackage,
  },
  {
    titleKey: "menu.promotions.section.title",
    descriptionKey: "menu.promotions.section.table.description",
    url: "/menu/promotions/section",
    icon: IconCategory,
  },
  {
    titleKey: "menu.promotions.collection.title",
    descriptionKey: "menu.promotions.collection.table.description",
    url: "/menu/promotions/collection",
    icon: IconFolder,
  },
] as const;

export default function PromotionsPage() {
  const { t } = useTranslation();
  const { data: activeOrganization, isLoading } = useActiveOrganization();

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 sm:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("sidebar.menu.promotions")}</CardTitle>
            <CardDescription>
              {t("menu.promotions.shared.notifications.activeOrgRequired")}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="space-y-1">
        <h1 className="font-serif font-semibold text-2xl tracking-tight">
          {t("sidebar.menu.promotions")}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t("menu.promotions.landing.description", {
            organization: activeOrganization.name,
          })}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {promotionTypes.map((type) => (
          <Link key={type.url} to={type.url}>
            <Card className="transition-colors hover:border-primary/50 hover:bg-muted/50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <type.icon className="size-5 text-muted-foreground" />
                  <CardTitle className="font-serif text-lg">{t(type.titleKey)}</CardTitle>
                </div>
                <CardDescription>{t(type.descriptionKey)}</CardDescription>
              </CardHeader>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
